import { useCallback, useEffect, useMemo, useState } from 'react';
import { clienteService } from '../services/clienteService';
import { sucursalService } from '../services/sucursalService';
import { useCenter } from '../contexts/CenterContext';
import { sortByNombre } from '../utils/sortByNombre';

export type FilterOption = { value: number; label: string };

type SucursalCombo = FilterOption & { clienteId: number };

export function useClienteSucursalFilter() {
  const [clienteOptions, setClienteOptions] = useState<FilterOption[]>([]);
  const [allSucursales, setAllSucursales] = useState<SucursalCombo[]>([]);
  const [selectedClienteId, setSelectedClienteId] = useState('');
  const { currentCenter } = useCenter();
  const centerId = currentCenter?.id;

  useEffect(() => {
    let cancelled = false;
    Promise.all([clienteService.getComboList(), sucursalService.getComboList()])
      .then(([clientes, sucursales]) => {
        if (cancelled) return;
        setClienteOptions(
          sortByNombre(clientes).map((c) => ({ value: c.id, label: c.nombre })),
        );
        setAllSucursales(
          sortByNombre(sucursales).map((s) => ({
            value: s.id,
            label: s.nombre,
            clienteId: s.clienteId,
          })),
        );
      })
      .catch(() => {
        if (!cancelled) {
          setClienteOptions([]);
          setAllSucursales([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [centerId]);

  const sucursalOptions = useMemo(() => {
    if (!selectedClienteId) return [];
    const id = Number(selectedClienteId);
    return sortByNombre(allSucursales.filter((s) => s.clienteId === id));
  }, [allSucursales, selectedClienteId]);

  useEffect(() => {
    setSelectedClienteId('');
  }, [centerId]);

  const loadSucursalesForCliente = useCallback(async (clienteId: string | number | undefined) => {
    setSelectedClienteId(clienteId ? String(clienteId) : '');
  }, []);

  return { clienteOptions, sucursalOptions, loadSucursalesForCliente };
}
